import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  Check, X, Infinity, Star, Loader2, ExternalLink,
  UtensilsCrossed, Palette, Plus, ShoppingBag, Truck,
  CreditCard, Users, BarChart3, ChefHat, Monitor,
  QrCode, Bell, Smartphone, Package, Tag, Megaphone,
  LayoutDashboard, DollarSign, UserCog, MessageSquare,
  MapPin, Shield, BookOpen, Gift, Receipt, TrendingUp,
  Instagram, Image
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  PLATFORM_FEATURES,
  FEATURE_CATEGORIES,
  type PlatformFeature,
} from "@/lib/platform-features";
import Navbar from "@/components/landing/Navbar";
import FooterSection from "@/components/landing/FooterSection";

/* ── feature key → tutorial id mapping ── */
const featureTutorialMap: Record<string, string> = {
  menu_digital: "cardapio",
  menu_customization: "aparencia",
  product_addons: "cardapio",
  product_images: "cardapio",
  ai_description: "cardapio",
  ai_image_generation: "cardapio",
  orders_dine_in: "pedidos",
  orders_takeout: "pedidos",
  orders_delivery: "delivery",
  orders_scheduled: "pedidos",
  kitchen_panel: "cozinha",
  order_tracking: "pedidos",
  pdv: "pdv",
  table_management: "mesas",
  waiter_call: "mesas",
  table_qrcode: "qrcode",
  waiter_module: "garcom",
  delivery_management: "delivery",
  delivery_tracking: "delivery",
  payment_pix: "pagamentos",
  payment_credit_card: "pagamentos",
  payment_mercadopago: "pagamentos",
  split_payment: "pagamentos",
  loyalty_program: "fidelidade",
  coupons: "cupons",
  customer_reactivation: "clientes",
  stock_management: "estoque",
  cost_reports: "relatorios",
  financial_management: "financeiro",
  advanced_reports: "relatorios",
  customer_management: "clientes",
  staff_management: "funcionarios",
  promo_popups: "popups",
  social_links: "redes-sociais",
  marketing_ai: "marketing-ai",
  ifood_integration: "ifood",
  whatsapp_notifications: "whatsapp",
  instagram_shop: "instagram-shop",
  whatsapp_catalog: "whatsapp-catalogo",
  live_support: "sugestoes",
  order_ratings: "avaliacoes",
  growth_mode: "crescimento",
};

/* ── feature key → icon mapping ── */
const featureIconMap: Record<string, React.ElementType> = {
  menu_digital: UtensilsCrossed,
  menu_customization: Palette,
  product_addons: Plus,
  product_images: UtensilsCrossed,
  ai_description: Star,
  ai_image_generation: Image,
  orders_dine_in: ShoppingBag,
  orders_takeout: ShoppingBag,
  orders_delivery: Truck,
  orders_scheduled: ShoppingBag,
  kitchen_panel: ChefHat,
  order_tracking: MapPin,
  pdv: LayoutDashboard,
  table_management: Monitor,
  waiter_call: Bell,
  table_qrcode: QrCode,
  waiter_module: UserCog,
  delivery_management: Truck,
  delivery_tracking: MapPin,
  payment_pix: CreditCard,
  payment_credit_card: CreditCard,
  payment_mercadopago: CreditCard,
  split_payment: Users,
  loyalty_program: Gift,
  coupons: Tag,
  customer_reactivation: Users,
  stock_management: Package,
  cost_reports: BarChart3,
  financial_management: DollarSign,
  advanced_reports: TrendingUp,
  customer_management: Users,
  staff_management: UserCog,
  promo_popups: Megaphone,
  social_links: Megaphone,
  marketing_ai: Megaphone,
  ifood_integration: Smartphone,
  whatsapp_notifications: MessageSquare,
  instagram_shop: Instagram,
  whatsapp_catalog: MessageSquare,
  live_support: MessageSquare,
  order_ratings: Star,
  growth_mode: TrendingUp,
};

/* ── Category icon ── */
const categoryIconMap: Record<string, React.ElementType> = {
  "Cardápio": UtensilsCrossed,
  "Pedidos": ShoppingBag,
  "Mesas": Monitor,
  "Delivery": Truck,
  "Pagamentos": CreditCard,
  "Fidelidade": Gift,
  "Gestão": BarChart3,
  "Marketing": Megaphone,
  "Integrações": Smartphone,
  "Suporte": MessageSquare,
  "Inteligência": TrendingUp,
};

interface Plan {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  price_monthly: number;
  price_yearly: number | null;
  max_orders_per_month: number | null;
  max_products: number | null;
  max_categories: number | null;
  features: Record<string, boolean> | string[] | null;
  is_featured: boolean;
  promo_price_monthly?: number | null;
  promo_price_yearly?: number | null;
  promo_label?: string | null;
  promo_ends_at?: string | null;
}

const Planos = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [billing, setBilling] = useState<"monthly" | "yearly">("monthly");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const fetchPlans = async () => {
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
        const res = await fetch(
          `${supabaseUrl}/rest/v1/plans?select=*&is_active=eq.true&order=sort_order.asc`,
          {
            headers: {
              apikey: supabaseKey,
              Authorization: `Bearer ${supabaseKey}`,
              Accept: "application/json",
            },
          }
        );
        if (!mounted) return;
        if (!res.ok) { setLoading(false); return; }
        const data = await res.json();
        setPlans(data || []);
      } catch (err) {
        console.error(err);
      }
      if (mounted) setLoading(false);
    };
    fetchPlans();
    return () => { mounted = false; };
  }, []);

  const planHasFeature = (plan: Plan, key: string): boolean => {
    if (!plan.features) return false;
    if (Array.isArray(plan.features)) return plan.features.includes(key);
    if (typeof plan.features === "object") return plan.features[key] === true;
    return false;
  };

  const hasPromo = (plan: Plan) => {
    if (!plan.promo_price_monthly && !plan.promo_price_yearly) return false;
    if (plan.promo_ends_at && new Date(plan.promo_ends_at) < new Date()) return false;
    return true;
  };

  const getPrice = (plan: Plan) => {
    const base = billing === "yearly" && plan.price_yearly != null ? plan.price_yearly : plan.price_monthly;
    if (hasPromo(plan)) {
      return billing === "yearly" && plan.promo_price_yearly
        ? plan.promo_price_yearly
        : plan.promo_price_monthly || base;
    }
    return base;
  };

  const formatPrice = (v: number) =>
    v === 0 ? "Grátis" : `R$${v.toFixed(0)}`;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16">
        <div className="container">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Badge variant="secondary" className="mb-4">Planos & Recursos</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Tudo que você precisa para <span className="text-primary">gerenciar seu restaurante</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Compare planos, veja todos os recursos disponíveis e escolha o ideal para o seu negócio.
            </p>

            {/* Billing toggle */}
            <div className="inline-flex items-center gap-1 bg-muted rounded-full p-1">
              <button
                onClick={() => setBilling("monthly")}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  billing === "monthly" ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Mensal
              </button>
              <button
                onClick={() => setBilling("yearly")}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  billing === "yearly" ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Anual <span className="text-xs opacity-80">(-20%)</span>
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Plan cards */}
      <section className="pb-16">
        <div className="container">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : plans.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">Nenhum plano disponível.</p>
          ) : (
            <div className={`grid gap-6 max-w-6xl mx-auto ${
              plans.length <= 3 ? "md:grid-cols-3" : "md:grid-cols-2 lg:grid-cols-4"
            }`}>
              {plans.map((plan, i) => {
                const price = getPrice(plan);
                const isPromo = hasPromo(plan);
                return (
                  <motion.div
                    key={plan.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className={`relative rounded-2xl p-6 border transition-all ${
                      plan.is_featured
                        ? "border-primary bg-gradient-card shadow-glow scale-[1.02]"
                        : "border-border bg-gradient-card shadow-card"
                    }`}
                  >
                    {plan.is_featured && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-primary text-xs font-semibold text-primary-foreground flex items-center gap-1">
                        <Star className="h-3 w-3" /> Mais Popular
                      </div>
                    )}
                    {isPromo && plan.promo_label && (
                      <div className="absolute -top-3 right-4 px-3 py-0.5 rounded-full bg-emerald-500 text-[10px] font-semibold text-white">
                        {plan.promo_label}
                      </div>
                    )}

                    <h3 className="font-display text-xl font-bold">{plan.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1 min-h-[40px]">{plan.description}</p>

                    <div className="mt-4 mb-2">
                      <span className="text-4xl font-bold">{formatPrice(price)}</span>
                      {price > 0 && (
                        <span className="text-muted-foreground">/{billing === "yearly" ? "ano" : "mês"}</span>
                      )}
                    </div>

                    <div className="flex gap-4 text-xs mb-4">
                      <div>
                        <span className="text-muted-foreground">Produtos: </span>
                        <span className="font-semibold">
                          {plan.max_products ?? <span className="inline-flex items-center gap-0.5"><Infinity className="h-3 w-3" />∞</span>}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Pedidos/mês: </span>
                        <span className="font-semibold">
                          {plan.max_orders_per_month
                            ? plan.max_orders_per_month.toLocaleString("pt-BR")
                            : <span className="inline-flex items-center gap-0.5"><Infinity className="h-3 w-3" />∞</span>}
                        </span>
                      </div>
                    </div>

                    <Button
                      onClick={() => navigate(`/cadastro?plan=${plan.id}`)}
                      className={`w-full h-11 font-semibold ${
                        plan.is_featured
                          ? "bg-gradient-primary text-primary-foreground hover:opacity-90"
                          : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                      }`}
                    >
                      {price === 0 ? "Começar Grátis" : "Começar Agora"}
                    </Button>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Feature comparison table */}
      <section className="py-16 bg-muted/30">
        <div className="container">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-3">
              Comparativo completo de recursos
            </h2>
            <p className="text-muted-foreground text-lg">
              {PLATFORM_FEATURES.length} recursos disponíveis organizados por categoria
            </p>
          </motion.div>

          {!loading && plans.length > 0 && (
            <div className="max-w-6xl mx-auto overflow-x-auto">
              <table className="w-full border-collapse">
                {/* Header */}
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-4 px-4 min-w-[280px]">
                      <span className="text-sm font-semibold text-muted-foreground">Recurso</span>
                    </th>
                    {plans.map((plan) => (
                      <th key={plan.id} className="text-center py-4 px-3 min-w-[120px]">
                        <span className={`text-sm font-bold ${plan.is_featured ? "text-primary" : ""}`}>
                          {plan.name}
                        </span>
                      </th>
                    ))}
                    <th className="text-center py-4 px-3 min-w-[90px]">
                      <span className="text-sm font-semibold text-muted-foreground">Tutorial</span>
                    </th>
                  </tr>
                </thead>

                {FEATURE_CATEGORIES.map((category) => {
                    const features = PLATFORM_FEATURES.filter(
                      (f) => f.category === category
                    );
                    const CategoryIcon = categoryIconMap[category] || BookOpen;

                    return (
                      <tbody key={category}>
                        <tr className="bg-muted/50">
                          <td colSpan={plans.length + 2} className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <CategoryIcon className="h-4 w-4 text-primary" />
                              <span className="font-bold text-sm">{category}</span>
                              <Badge variant="outline" className="text-[10px] ml-1">
                                {features.length}
                              </Badge>
                            </div>
                          </td>
                        </tr>
                        {features.map((feature) => {
                          const Icon = featureIconMap[feature.key] || BookOpen;
                          const tutorialId = featureTutorialMap[feature.key];
                          return (
                            <tr
                              key={feature.key}
                              className="border-b border-border/50 hover:bg-muted/20 transition-colors"
                            >
                              <td className="py-3 px-4">
                                <div className="flex items-start gap-3">
                                  <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                                  <div>
                                    <span className="text-sm font-medium">{feature.label}</span>
                                    <p className="text-xs text-muted-foreground mt-0.5">{feature.description}</p>
                                  </div>
                                </div>
                              </td>
                              {plans.map((plan) => {
                                const has = planHasFeature(plan, feature.key);
                                return (
                                  <td key={plan.id} className="text-center py-3 px-3">
                                    {has ? (
                                      <Check className="h-5 w-5 text-primary mx-auto" />
                                    ) : (
                                      <X className="h-4 w-4 text-muted-foreground/40 mx-auto" />
                                    )}
                                  </td>
                                );
                              })}
                              <td className="text-center py-3 px-3">
                                {tutorialId ? (
                                  <Link
                                    to={`/tutoriais?t=${tutorialId}`}
                                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                                  >
                                    Ver <ExternalLink className="h-3 w-3" />
                                  </Link>
                                ) : (
                                  <span className="text-xs text-muted-foreground/60">—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    );
                  })}

                  {/* Limits */}
                  <tbody>
                    <tr className="bg-muted/50">
                      <td colSpan={plans.length + 2} className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <BarChart3 className="h-4 w-4 text-primary" />
                          <span className="font-bold text-sm">Limites</span>
                        </div>
                      </td>
                    </tr>
                    <tr className="border-b border-border/50">
                      <td className="py-3 px-4 text-sm font-medium">Produtos cadastrados</td>
                      {plans.map((plan) => (
                        <td key={plan.id} className="text-center py-3 px-3 text-sm font-semibold">
                          {plan.max_products ?? <span className="text-primary">Ilimitado</span>}
                        </td>
                      ))}
                      <td />
                    </tr>
                    <tr className="border-b border-border/50">
                      <td className="py-3 px-4 text-sm font-medium">Categorias</td>
                      {plans.map((plan) => (
                        <td key={plan.id} className="text-center py-3 px-3 text-sm font-semibold">
                          {plan.max_categories ?? <span className="text-primary">Ilimitado</span>}
                        </td>
                      ))}
                      <td />
                    </tr>
                    <tr className="border-b border-border/50">
                      <td className="py-3 px-4 text-sm font-medium">Pedidos por mês</td>
                      {plans.map((plan) => (
                        <td key={plan.id} className="text-center py-3 px-3 text-sm font-semibold">
                          {plan.max_orders_per_month
                            ? plan.max_orders_per_month.toLocaleString("pt-BR")
                            : <span className="text-primary">Ilimitado</span>}
                        </td>
                      ))}
                      <td />
                    </tr>
                  </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="container">
          <motion.div
            className="text-center max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold mb-4">Ainda tem dúvidas?</h2>
            <p className="text-muted-foreground mb-8">
              Explore nossos tutoriais detalhados ou entre em contato com nossa equipe.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-gradient-primary text-primary-foreground">
                <Link to="/tutoriais">Ver Tutoriais</Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/contato">Falar com a Equipe</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <FooterSection />
    </div>
  );
};

export default Planos;
